/**
 * Skills — lista (skills.html): catálogo real de la API (nombre, capa,
 * estado, versión, draft) + TELEMETRÍA REAL de las trazas (`/studio/skills/
 * telemetria`): implantaciones = casas distintas, disparos de la ventana con
 * su delta, % de propuestas confirmadas. Ya no hay semilla.
 * Lo que no se mide se dice: los conflictos de precedencia van en «—» con
 * glosa (la traza no guarda qué OTROS métodos casaron) y el cero medido se
 * escribe 0 — ver `telemetria.ts`.
 * Estados: lista / vacío primera vez / cargando / error con copia stale; la
 * telemetría tiene los suyos propios (el catálogo se ve aunque ella caiga).
 * ?estado= fuerza un estado para revisión (contrato del prototipo).
 */
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { SkillTelemetryRow, StudioSkill } from "@skein/contracts";
import { Badge, BadgeGroup, Banner, Button, CellTitle, Delta, Empty, Help, Mono, PageBtn, PageHead, Pagination, RowActions, SearchInput, Select, Skeleton, Table, TdNum, Th, Tip, Toolbar, useSlashFocus, useToast, type SortDir } from "@skein/ui";
import { useSkills, useSkillsTelemetria } from "../../api/queries";
import { deltaTexto, deltaTono, diasVentana, filaDe, indexar, sinMetodo, valorOrden, ventana, type ClaveOrden } from "./telemetria";

type Row = StudioSkill & { t: SkillTelemetryRow };
type SortKey = ClaveOrden;
const SIN_RESUELTAS = "sin propuestas resueltas todavía";
const SIN_CONFLICTOS = "aún no se mide: la traza no guarda qué otros métodos casaron";
const SIN_TELEMETRIA = "no llego a la telemetría: no es un cero";
const CAPAS = [
  { value: "", label: "Todas las capas" },
  { value: "general", label: <Mono>general</Mono> },
  { value: "ceo", label: <Mono>ceo</Mono> },
  { value: "cfo", label: <Mono>cfo</Mono> },
  { value: "production", label: <Mono>production</Mono> },
  { value: "commercial", label: <Mono>commercial</Mono> },
] as const;
const ESTADOS = [
  { value: "", label: "Todos los estados" },
  { value: "published", label: <Mono>published</Mono> },
  { value: "draft", label: <>con <Mono>draft</Mono></> },
  { value: "retired", label: <Mono>retired</Mono> },
] as const;

function VersionChip({ s }: { s: StudioSkill }) {
  if (s.estado === "draft") return <BadgeGroup><Badge tone="accent">draft — sin publicar</Badge></BadgeGroup>;
  if (s.estado === "retired") return <BadgeGroup><Badge>{`v${s.version} · retired`}</Badge></BadgeGroup>;
  return <BadgeGroup><Badge tone="ok">{`v${s.version} · published`}</Badge>{s.hasDraft && <Badge tone="accent">+draft</Badge>}</BadgeGroup>;
}
function SkeletonRows() {
  const cell = (w: string) => <Skeleton style={{ width: w, marginLeft: "auto" }} />;
  return (
    <tbody>
      {Array.from({ length: 6 }, (_, i) => (
        <tr key={i}>
          <td><Skeleton style={{ width: "11rem" }} /><Skeleton style={{ width: "7rem", marginTop: "var(--sk-sp-1)" }} /></td>
          <td><Skeleton style={{ width: "4rem" }} /></td>
          <td><Skeleton style={{ width: "7rem" }} /></td>
          <TdNum>{cell("2rem")}</TdNum><TdNum>{cell("2.5rem")}</TdNum><TdNum>{cell("3rem")}</TdNum><TdNum>{cell("2rem")}</TdNum><td />
        </tr>
      ))}
    </tbody>
  );
}

export function SkillsPage() {
  const skills = useSkills();
  const tel = useSkillsTelemetria();
  const navigate = useNavigate();
  const say = useToast();
  const search = useSearch({ strict: false }) as { estado?: string };
  const [q, setQ] = useState("");
  const [capa, setCapa] = useState("");
  const [estado, setEstado] = useState("");
  const [orden, setOrden] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: "disparos", dir: -1 });
  const inputRef = useSlashFocus();

  const data = skills.data;
  const idx = useMemo(() => indexar(tel.data), [tel.data]);
  const rows = useMemo<Row[]>(() => {
    const xs = (data ?? []).map((s) => ({ ...s, t: filaDe(idx, s.slug) }));
    const query = q.trim().toLowerCase();
    const filtered = xs.filter((s) =>
      (!query || s.nombre.toLowerCase().includes(query) || s.slug.includes(query)) &&
      (!capa || s.capa === capa) &&
      (!estado || (estado === "draft" ? s.hasDraft || s.estado === "draft" : s.estado === estado)));
    return filtered.sort((a, b) => { const va = valorOrden(a, orden.k), vb = valorOrden(b, orden.k); return (va < vb ? -1 : va > vb ? 1 : 0) * orden.dir; });
  }, [data, idx, q, capa, estado, orden]);

  const sort = (k: SortKey) => setOrden((o) => (o.k === k ? { k, dir: o.dir === 1 ? -1 : 1 } : { k, dir: k === "nombre" ? 1 : -1 }));
  const sorted = (k: SortKey): SortDir | null => (orden.k === k ? (orden.dir < 0 ? "descending" : "ascending") : null);

  // estado de la pantalla (con override ?estado= para revisión)
  const forced = search.estado;
  const view = forced === "vacio" || forced === "cargando" || forced === "error" ? forced
    : skills.isPending ? "cargando"
    : skills.isError && !data ? "error-sin-datos"
    : skills.isError ? "error"
    : (data?.length ?? 0) === 0 ? "vacio" : "lista";
  // la telemetría va por su cuenta: puede estar cargando o caída con el catálogo entero delante
  const telView = forced === "cargando" ? "cargando" : forced === "error" ? "error"
    : tel.isPending ? "cargando" : tel.isError && !tel.data ? "error" : "datos";
  const t = telView === "datos" ? tel.data : undefined;
  const hueco = sinMetodo(t);
  const dv = diasVentana(t);

  const nPub = data?.filter((s) => s.estado === "published").length ?? 0;
  const nDraft = data?.filter((s) => s.estado === "draft").length ?? 0;
  const nRet = data?.filter((s) => s.estado === "retired").length ?? 0;

  const abrir = (slug: string) => void navigate({ to: "/skills/$slug", params: { slug } });
  // el prototipo contrata cabeceras planas y filas inertes fuera de la lista viva
  const interactive = view === "lista";
  /** celda de métrica: esqueleto mientras carga, «—» si la telemetría no llega. */
  const metrica = (v: ReactNode, w = "2.5rem") =>
    telView === "cargando" ? <Skeleton style={{ width: w, marginLeft: "auto" }} /> : telView === "error" ? "—" : v;
  const tabla = (
    <Table stale={view.startsWith("error")} busy={view === "cargando"} label={view === "cargando" ? "Cargando skills" : "Skills"}>
      <thead><tr>
        <Th sortable={interactive} sorted={sorted("nombre")} onSort={() => sort("nombre")}>Skill</Th>
        <Th>Capa</Th>
        <Th>Versión</Th>
        <Th num sortable={interactive} sorted={sorted("casas")} onSort={() => sort("casas")}>Implantaciones</Th>
        <Th num sortable={interactive} sorted={sorted("disparos")} onSort={() => sort("disparos")}>{dv === null ? "Disparos" : `Disparos · ${dv}d`}</Th>
        <Th num sortable={interactive} sorted={sorted("conf")} onSort={() => sort("conf")}>Confirmadas</Th>
        <Th num>Conflictos</Th>
        <Th aria-label="Acciones" />
      </tr></thead>
      {view === "cargando" ? <SkeletonRows /> : (
        <tbody>
          {rows.map((s) => {
            const dt = deltaTexto(s.t.delta);
            return (
            <tr key={s.slug} tabIndex={interactive ? 0 : undefined} onClick={interactive ? () => abrir(s.slug) : undefined} onKeyDown={interactive ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) abrir(s.slug); } : undefined}>
              <CellTitle title={s.nombre} sub={s.slug} />
              <td><Badge>{s.capa}</Badge></td>
              <td><VersionChip s={s} /></td>
              <TdNum>{metrica(s.t.casas, "2rem")}</TdNum>
              <TdNum>{metrica(<>{s.t.disparos}{dt !== null && s.t.delta !== null && <> <Delta tone={deltaTono(s.t.delta)}>{dt}</Delta></>}</>)}</TdNum>
              <TdNum>{metrica(s.t.confirmadasPct !== null ? `${s.t.confirmadasPct}%` : <Tip tip={SIN_RESUELTAS}>—</Tip>, "3rem")}</TdNum>
              <TdNum><Tip tip={telView === "error" ? SIN_TELEMETRIA : SIN_CONFLICTOS}>—</Tip></TdNum>
              {interactive ? (
                <RowActions>
                  <Button variant="quiet" size="s" onClick={(e) => { e.stopPropagation(); abrir(s.slug); }}>Abrir</Button>
                  <Button variant="quiet" size="s" onClick={(e) => { e.stopPropagation(); say(`El sandbox con ${s.slug} llega con F5b.`); }}>Sandbox</Button>
                </RowActions>
              ) : <td />}
            </tr>
          );})}
          {rows.length === 0 && view === "lista" && (
            <tr><td colSpan={8}><Empty inline title="Nada casa con ese filtro." style={{ margin: "var(--sk-sp-3)" }}>Prueba a quitar la capa o el estado.</Empty></td></tr>
          )}
        </tbody>
      )}
    </Table>
  );

  return (
    <>
      <PageHead
        title="Skills"
        meta={view === "vacio" ? <Badge>0 en catálogo</Badge>
          : !data ? (view === "cargando" ? <Skeleton style={{ width: "18rem", height: "1.25em" }} /> : undefined)
          : <>
          <Badge>{data.length} en catálogo</Badge>
          {nPub > 0 && <Badge tone="ok">{nPub} published</Badge>}
          {nDraft > 0 && <Badge tone="accent">{nDraft} draft</Badge>}
          {nRet > 0 && <Badge>{nRet} retired</Badge>}
          {t && <Badge>uso · {ventana(t)}</Badge>}
        </>}
        actions={<>
          <Button onClick={() => say("El sandbox del catálogo llega con F5b.")}>Probar en sandbox</Button>
          <Button variant="primary" onClick={() => say("El alta de skill llega con F5b.")}>Nueva skill…</Button>
        </>}
      />
      <div className="listzone">
        {view !== "vacio" && view !== "error-sin-datos" && (
          <Toolbar>
            <SearchInput inputRef={inputRef} style={{ flex: 1, minWidth: "14rem" }} placeholder="Buscar por nombre, slug o keyword…" aria-label="Buscar skills" shortcut="/" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select ariaLabel="Filtrar por capa" style={{ width: "11rem" }} value={capa} onChange={setCapa} options={CAPAS} />
            <Select ariaLabel="Filtrar por estado" style={{ width: "11rem" }} value={estado} onChange={setEstado} options={ESTADOS} />
          </Toolbar>
        )}
        {view === "vacio" && (
          <Empty glyph="✳" title="Aún no hay skills en el catálogo">
            Un skill es el método de la casa para un tema: cómo razonar la erosión de margen, la tensión de tesorería…
            El asesor responde igualmente sin ellos — con criterio general y degradación explícita.
            <Button variant="primary" onClick={() => say("El alta del primer skill llega con F5b.")}>Crear el primer skill…</Button>
          </Empty>
        )}
        {view === "error-sin-datos" && (
          <Banner tone="danger"><b>No llego al catálogo.</b> Y aún no tengo una copia que enseñarte. Reintento solo cada 30 s — o fuerza uno ahora. <Button variant="link" onClick={() => void skills.refetch()}>reintentar ahora</Button></Banner>
        )}
        {view === "error" && (
          <Banner tone="danger"><b>No llego al catálogo.</b> La lista que ves puede estar desactualizada. Reintento solo cada 30 s — o fuerza uno ahora. <Button variant="link" onClick={() => void skills.refetch()}>reintentar ahora</Button></Banner>
        )}
        {telView === "error" && view !== "vacio" && view !== "error-sin-datos" && (
          <Banner tone="warn"><b>No llego a la telemetría.</b> El catálogo que ves es real; los números de uso, no los tengo. Reintento solo cada 30 s — o fuerza uno ahora. <Button variant="link" onClick={() => void tel.refetch()}>reintentar ahora</Button></Banner>
        )}
        {(view === "lista" || view === "cargando" || view === "error") && (
          <section>
            {tabla}
            {view === "lista" && (
              <div className="list-foot" style={{ marginTop: "var(--sk-sp-4)" }}>
                <Pagination meta={rows.length ? `1–${rows.length} de ${rows.length}` : `0 de ${data?.length ?? 0}`}>
                  <PageBtn disabled>‹</PageBtn>
                  <PageBtn current>1</PageBtn>
                  <PageBtn disabled>›</PageBtn>
                </Pagination>
              </div>
            )}
          </section>
        )}
        {(view === "lista" || view === "error") && (
          <section aria-label="Cómo leer esta tabla" style={{ marginTop: "var(--sk-sp-4)", maxWidth: "46rem" }}>
            {hueco && (
              <Help>
                {hueco.n > 0
                  ? <><b>{hueco.n} respuestas sin método</b> en los últimos {hueco.dias} días, de {hueco.de} que dio el asesor: respondió con criterio general porque ningún skill casaba. Es lo que falta por escribir. <Button variant="link" onClick={() => say("Las respuestas sin método, una a una y con su traza, se listan en Auditoría cuando tenga su endpoint (F5c). Hoy la telemetría solo las cuenta.")}>ver cuáles</Button></>
                  : <><b>Ninguna respuesta se quedó sin método</b> en los últimos {hueco.dias} días: el catálogo cubrió las {hueco.de} que dio el asesor.</>}
              </Help>
            )}
            {/* el idioma de la tabla se lee SIEMPRE — sobre todo cuando las cuatro
                columnas son «—», que es justo cuando `hueco` no existe */}
            <Help style={{ marginTop: hueco ? "var(--sk-sp-2)" : undefined }}>
              El chip junto a <b>Disparos</b> es la variación frente a {dv === null ? "la ventana anterior" : `los ${dv} días anteriores`} —verde sube, rojo baja—: dirección, no juicio.
              «—» marca lo que <b>no se mide</b>, nunca un cero: los conflictos de precedencia todavía no se miden —la traza no guarda qué OTROS métodos casaron— y «Confirmadas» calla mientras ninguna propuesta se haya resuelto. El cero medido se escribe 0.
            </Help>
          </section>
        )}
      </div>
    </>
  );
}
